/**
 * AchievementCelebrationModal — full-screen overlay shown when the
 * user unlocks an achievement. Three jobs:
 *
 *   1) Fire a confetti burst the moment it mounts (canvas-confetti).
 *   2) Spotlight the badge: large icon, name, description, XP earned.
 *   3) Offer a one-tap share that builds a 1080×1920 PNG and either
 *      uses Web Share API (mobile) or downloads (desktop).
 *
 * Auto-dismisses on Escape and on backdrop click; otherwise stays up
 * until the user taps the close button. Multiple unlocks queue in the
 * CelebrationContext, so this component only renders the active one.
 *
 * No persistent state — context owns the queue, this is pure UI.
 */
import { useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { Share2, X, Sparkles, Download } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSounds } from '../hooks/useSounds';

const fireConfetti = () => {
  // Two-burst pattern: a wide centre burst, then a smaller delayed top burst.
  // Keeps GPU work bounded vs continuous emitters.
  const defaults = { spread: 70, ticks: 200, gravity: 0.8, decay: 0.94, startVelocity: 30 };
  confetti({ ...defaults, particleCount: 80, origin: { x: 0.5, y: 0.6 }, colors: ['#002FA7', '#F59E0B', '#10B981', '#FFD700'] });
  setTimeout(() => {
    confetti({ ...defaults, particleCount: 50, origin: { x: 0.3, y: 0.4 }, colors: ['#FFD700', '#F59E0B'] });
    confetti({ ...defaults, particleCount: 50, origin: { x: 0.7, y: 0.4 }, colors: ['#002FA7', '#4F2BB8'] });
  }, 220);
};

/**
 * Builds the 1080×1920 share card on a hidden canvas and returns it as
 * a Blob. Keeps the markup decoupled from share branding so we can
 * tweak the design without touching the rest of the modal.
 */
const buildShareCard = async (badge, displayName) => {
  const W = 1080, H = 1920;
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');

  // Gradient backdrop (iStudyPro deep-blue → violet)
  const grad = ctx.createLinearGradient(0, 0, W, H);
  grad.addColorStop(0, '#002FA7');
  grad.addColorStop(1, '#4F2BB8');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // Soft white glow centre
  const radial = ctx.createRadialGradient(W / 2, H / 2, 100, W / 2, H / 2, 800);
  radial.addColorStop(0, 'rgba(255,255,255,0.18)');
  radial.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = radial;
  ctx.fillRect(0, 0, W, H);

  // Brand wordmark — top
  ctx.fillStyle = 'rgba(255,255,255,0.85)';
  ctx.font = 'bold 56px -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui';
  ctx.textAlign = 'center';
  ctx.fillText('iStudyPRO', W / 2, 220);
  ctx.fillStyle = 'rgba(255,255,255,0.55)';
  ctx.font = '32px -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui';
  ctx.fillText('Record your lecture. We do the rest.', W / 2, 280);

  // Big rosette circle
  ctx.fillStyle = 'rgba(255,215,0,0.15)';
  ctx.beginPath();
  ctx.arc(W / 2, H / 2 - 40, 280, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,215,0,0.4)';
  ctx.lineWidth = 4;
  ctx.stroke();

  // Badge emoji — uses Apple Color Emoji on iOS / Segoe UI Emoji on Win
  const emoji = (badge?.icon || '🏆').toString();
  ctx.font = '240px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", emoji';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(emoji, W / 2, H / 2 - 40);

  // Achievement label
  ctx.fillStyle = '#FFD700';
  ctx.font = 'bold 38px -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui';
  ctx.textBaseline = 'alphabetic';
  ctx.fillText('ACHIEVEMENT UNLOCKED', W / 2, H / 2 + 320);

  // Achievement name (with word-wrap for long titles)
  ctx.fillStyle = '#FFFFFF';
  ctx.font = 'bold 80px -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui';
  const name = (badge?.name || 'Achievement').toString();
  const maxWidth = W - 200;
  const words = name.split(' ');
  let line = '';
  const lines = [];
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  lines.slice(0, 3).forEach((l, i) => {
    ctx.fillText(l, W / 2, H / 2 + 410 + i * 90);
  });

  // Footer: user name
  if (displayName) {
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    ctx.font = '36px -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui';
    ctx.fillText(`— ${displayName}`, W / 2, H - 180);
  }
  ctx.fillStyle = 'rgba(255,255,255,0.4)';
  ctx.font = '26px -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui';
  ctx.fillText('istudypro.net', W / 2, H - 120);

  return new Promise((resolve) => canvas.toBlob(resolve, 'image/png', 0.92));
};

const AchievementCelebrationModal = ({ badge, onClose }) => {
  const { user } = useAuth();
  const { play } = useSounds();
  const hasFiredRef = useRef(null);

  // Fire confetti once per badge presented.
  useEffect(() => {
    if (!badge) {
      hasFiredRef.current = null;
      return;
    }
    const key = badge.id || badge.name;
    if (hasFiredRef.current === key) return;
    hasFiredRef.current = key;
    fireConfetti();
    play('achievement');
  }, [badge, play]);

  // ESC to dismiss
  useEffect(() => {
    if (!badge) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [badge, onClose]);

  const handleShare = useCallback(async () => {
    try {
      const blob = await buildShareCard(badge, user?.name);
      if (!blob) return;
      const file = new File([blob], 'istudypro-achievement.png', { type: 'image/png' });
      // Prefer Web Share API on mobile — falls back to download.
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'Achievement unlocked on iStudyPRO',
          text: `Just unlocked "${badge?.name}" on iStudyPRO 🎯`,
        });
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'istudypro-achievement.png';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.warn('share failed:', e?.message);
    }
  }, [badge, user?.name]);

  return (
    <AnimatePresence>
      {badge && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="achievement-celebration"
        >
          {/* Backdrop */}
          <motion.button
            type="button"
            aria-label="Close celebration"
            className="absolute inset-0 bg-gradient-to-br from-[#002FA7]/95 to-[#4F2BB8]/95 backdrop-blur-sm"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="achievement-celebration-backdrop"
          />

          {/* Card */}
          <motion.div
            className="relative max-w-md w-full bg-white rounded-3xl shadow-2xl p-8 text-center overflow-hidden"
            initial={{ scale: 0.85, y: 30, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 240, damping: 22 }}
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute top-4 right-4 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
              data-testid="achievement-close"
              aria-label="Close"
            >
              <X className="w-4 h-4 text-slate-600" />
            </button>

            {/* Glow ring + emoji */}
            <div className="relative mx-auto w-32 h-32 mt-2">
              <motion.div
                className="absolute inset-0 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 blur-2xl opacity-50"
                animate={{ scale: [1, 1.15, 1] }}
                transition={{ duration: 2.4, repeat: Infinity }}
                aria-hidden
              />
              <motion.div
                className="relative w-full h-full rounded-full bg-gradient-to-br from-amber-100 to-amber-300 flex items-center justify-center text-7xl"
                initial={{ rotate: -10 }}
                animate={{ rotate: [0, -6, 6, 0] }}
                transition={{ duration: 1.6, repeat: 2 }}
              >
                {badge.icon || '🏆'}
              </motion.div>
            </div>

            <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-amber-600 mt-6 flex items-center justify-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" aria-hidden /> Achievement unlocked
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[var(--isp-heading)] mt-2 leading-tight" data-testid="achievement-name">
              {badge.name || 'Achievement'}
            </h2>
            {badge.description && (
              <p className="text-sm text-[var(--isp-body)] mt-2 max-w-sm mx-auto">
                {badge.description}
              </p>
            )}
            {badge.xp_reward ? (
              <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200">
                <span className="text-xs font-bold text-emerald-700">+{badge.xp_reward} XP</span>
              </div>
            ) : null}

            <div className="mt-6 flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={handleShare}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#002FA7] hover:bg-[#00227A] text-white font-semibold text-sm transition-colors"
                data-testid="achievement-share-btn"
              >
                <Share2 className="w-4 h-4" /> Share
                <Download className="w-3.5 h-3.5 opacity-50" />
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 inline-flex items-center justify-center px-4 py-3 rounded-xl border border-slate-200 hover:bg-slate-50 text-[var(--isp-heading)] font-semibold text-sm transition-colors"
                data-testid="achievement-continue"
              >
                Continue studying
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AchievementCelebrationModal;
